"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import { Search, X, Loader2, ArrowRight, Users, Trophy, User } from "lucide-react";
import { useLabels, useLocale } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { translatePosition } from "@/utilities/mappers/squad";
import { SITE_TYPE_BASIC, SITE_TYPE_FOOTBALL_COMPETITION } from "@/config/siteTypes";

interface ArticleResult {
    id: number;
    title: string;
    slug: string;
    heroImage: { url: string; alt: string | null } | null;
    categories: { id: number; title: string }[];
    publishedAt: string | null;
}

interface EntityResult {
    entityId: string;
    name: string;
    entityType?: string;
    subtitle?: string;
    imageUrl?: string;
    position?: string;
}

interface SearchToggleProps {
    basePath: string;
    variant?: "dark" | "light" | "branded";
}

const TOP_ENTITY_LIMIT = 3;
const TOP_ARTICLES_LIMIT = 4;

export function SearchToggle({ basePath: _basePath, variant = "dark" }: SearchToggleProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [articles, setArticles] = useState<ArticleResult[]>([]);
    const [entities, setEntities] = useState<EntityResult[]>([]);
    const [articlesLoading, setArticlesLoading] = useState(false);
    const [entitiesLoading, setEntitiesLoading] = useState(false);
    const [bothResolved, setBothResolved] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const mobileRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const requestIdRef = useRef(0);

    const labels = useLabels();
    const locale = useLocale();
    const nav = useNavigation();
    const { siteType } = useTenantSettings();
    const isCompetitionSite = siteType === SITE_TYPE_FOOTBALL_COMPETITION;
    const isBasicSite = siteType === SITE_TYPE_BASIC;

    const textClass =
        variant === "light" ? "text-white" : variant === "branded" ? "text-primary-foreground" : "text-foreground";

    const hoverBgClass =
        variant === "light"
            ? "hover:bg-white/10"
            : variant === "branded"
              ? "hover:bg-primary-foreground/10"
              : "hover:bg-muted";

    const close = useCallback(() => {
        setIsOpen(false);
        setQuery("");
        setArticles([]);
        setEntities([]);
        setBothResolved(false);
    }, []);

    // Close on click outside (desktop only)
    useEffect(() => {
        if (!isOpen) return;
        const handleMouseDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (mobileRef.current?.contains(target)) return;
            if (containerRef.current?.contains(target)) return;
            close();
        };
        document.addEventListener("mousedown", handleMouseDown);
        return () => document.removeEventListener("mousedown", handleMouseDown);
    }, [isOpen, close]);

    // Close on Escape
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") close();
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, close]);

    // Focus input when opened
    useEffect(() => {
        if (isOpen) {
            requestAnimationFrame(() => inputRef.current?.focus());
        }
    }, [isOpen]);

    // Lock body scroll when mobile modal is open
    useEffect(() => {
        if (!isOpen) return;
        const isMobile = window.innerWidth < 768;
        if (!isMobile) return;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "";
        };
    }, [isOpen]);

    // Debounced parallel search
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);

        const trimmed = query.trim();
        if (trimmed.length < 2) {
            requestIdRef.current++;
            setArticles([]);
            setEntities([]);
            setArticlesLoading(false);
            setEntitiesLoading(false);
            setBothResolved(false);
            return;
        }

        setArticlesLoading(true);
        setEntitiesLoading(true);
        setBothResolved(false);

        debounceRef.current = setTimeout(async () => {
            const requestId = ++requestIdRef.current;

            const articlesPromise = fetch(
                `/api/posts/search?q=${encodeURIComponent(trimmed)}&locale=${locale}&limit=${TOP_ARTICLES_LIMIT}`
            )
                .then((r) => r.json())
                .catch(() => null);

            // Basic site type has no team/competition/player pages — skip the
            // entity search entirely. The promise still resolves so bothResolved
            // fires and the "See all" CTA appears.
            const entitiesPromise = isBasicSite
                ? Promise.resolve(null)
                : fetch(
                      `/api/entities/search?q=${encodeURIComponent(trimmed)}&type=team,athlete,competition&locale=${locale}`
                  )
                      .then((r) => r.json())
                      .catch(() => null);

            articlesPromise.then((data) => {
                if (requestId !== requestIdRef.current) return;
                setArticles(data?.results || []);
                setArticlesLoading(false);
            });

            entitiesPromise.then((data) => {
                if (requestId !== requestIdRef.current) return;
                const allEntities: EntityResult[] = (data?.results || []).filter((e: EntityResult) =>
                    e.entityId.startsWith("fb:")
                );
                const filtered = isCompetitionSite
                    ? allEntities.filter((e) => e.entityType !== "competition")
                    : allEntities;
                setEntities(filtered.slice(0, TOP_ENTITY_LIMIT));
                setEntitiesLoading(false);
            });

            // Track both-resolved for the "See all" button
            Promise.all([articlesPromise, entitiesPromise]).then(() => {
                if (requestId !== requestIdRef.current) return;
                setBothResolved(true);
            });
        }, 300);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [query, locale, isCompetitionSite, isBasicSite]);

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return "";
        return new Date(dateStr).toLocaleDateString(locale, {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    };

    const entityHref = (entity: EntityResult): string | null => {
        switch (entity.entityType) {
            case "team":
                return nav.team(entity.entityId);
            case "athlete":
                return nav.player(entity.entityId);
            case "competition":
                if (isCompetitionSite) return null;
                try {
                    return nav.footballCompetition(entity.entityId);
                } catch {
                    return null;
                }
            default:
                return null;
        }
    };

    const entityIcon = (entity: EntityResult) =>
        entity.entityType === "team" ? Users : entity.entityType === "athlete" ? User : Trophy;

    if (!isOpen) {
        return (
            <button
                onClick={() => setIsOpen(true)}
                className={`p-2 rounded-lg ${hoverBgClass} transition cursor-pointer ${textClass}`}
                aria-label={labels.common?.search || "Search"}
            >
                <Search className="w-5 h-5" />
            </button>
        );
    }

    const isLoading = articlesLoading || entitiesLoading;
    const hasAnyResult = entities.length > 0 || articles.length > 0;
    const seeAllHref = nav.search(query.trim());
    const seeAllText = (labels.search?.seeAllFor || 'See all results for "{query}"').replace(
        "{query}",
        query.trim()
    );

    const resultsContent = query.trim().length >= 2 && (
        <div className="flex-1 overflow-y-auto md:absolute md:right-0 md:top-full md:mt-2 md:w-96 md:max-h-[28rem] bg-card md:border md:border-border md:rounded-lg md:shadow-lg md:z-50 md:overflow-hidden flex flex-col">
            <div className="flex-1 md:overflow-y-auto">
                {isLoading && !hasAnyResult ? (
                    <div className="flex items-center justify-center py-6">
                        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                ) : !hasAnyResult && bothResolved ? (
                    <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                        {labels.common?.noResults || "No results found"}
                    </div>
                ) : (
                    <>
                        {entities.length > 0 && (
                            <div>
                                <div className="px-4 pt-3 pb-1 text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                                    {labels.search?.topResults || "Top results"}
                                </div>
                                {entities.map((entity) => {
                                    const href = entityHref(entity);
                                    const Icon = entityIcon(entity);
                                    const subtitle =
                                        entity.entityType === "athlete" && entity.position
                                            ? translatePosition(entity.position, labels.teamPage?.playerPositions)
                                            : entity.subtitle;
                                    const inner = (
                                        <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted transition">
                                            {entity.imageUrl ? (
                                                <Image
                                                    src={entity.imageUrl}
                                                    alt={entity.name}
                                                    width={40}
                                                    height={40}
                                                    className="w-10 h-10 rounded object-contain shrink-0 bg-muted/30 p-0.5"
                                                    unoptimized
                                                />
                                            ) : (
                                                <div className="w-10 h-10 rounded bg-muted/40 shrink-0 flex items-center justify-center">
                                                    <Icon className="w-5 h-5 text-muted-foreground" />
                                                </div>
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-medium text-foreground truncate">
                                                    {entity.name}
                                                </div>
                                                {subtitle && (
                                                    <div className="text-xs text-muted-foreground truncate">
                                                        {subtitle}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                    return href ? (
                                        <Link prefetch={false} key={entity.entityId} href={href} onClick={close}>
                                            {inner}
                                        </Link>
                                    ) : (
                                        <div key={entity.entityId}>{inner}</div>
                                    );
                                })}
                            </div>
                        )}

                        {articles.length > 0 && (
                            <div className={entities.length > 0 ? "border-t border-border mt-1 pt-1" : ""}>
                                <div className="px-4 pt-3 pb-1 text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                                    {labels.search?.latestArticles || "Latest articles"}
                                </div>
                                {articles.map((article) => (
                                    <Link
                                        prefetch={false}
                                        key={article.id}
                                        href={nav.post(article)}
                                        onClick={close}
                                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted transition"
                                    >
                                        {article.heroImage?.url ? (
                                            <Image
                                                src={article.heroImage.url}
                                                alt={article.heroImage.alt || article.title}
                                                width={48}
                                                height={48}
                                                className="w-12 h-12 rounded object-cover shrink-0"
                                                unoptimized
                                            />
                                        ) : (
                                            <div className="w-12 h-12 rounded bg-muted shrink-0" />
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium text-foreground line-clamp-2">
                                                {article.title}
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-0.5">
                                                {article.categories[0]?.title}
                                                {article.categories[0]?.title && article.publishedAt && " · "}
                                                {formatDate(article.publishedAt)}
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            {bothResolved && (
                <Link
                    prefetch={false}
                    href={seeAllHref}
                    onClick={close}
                    className="flex items-center justify-between gap-2 px-4 py-3 border-t border-border bg-muted/30 hover:bg-muted transition text-sm font-medium text-foreground sticky bottom-0"
                >
                    <span className="truncate">{seeAllText}</span>
                    <ArrowRight className="w-4 h-4 shrink-0 text-primary" />
                </Link>
            )}
        </div>
    );

    return (
        <>
            {/* Mobile: full-screen overlay */}
            <div ref={mobileRef} className="md:hidden fixed inset-0 z-9999 bg-background flex flex-col">
                <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
                    <Search className="w-5 h-5 shrink-0 text-muted-foreground" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={labels.common?.search || "Search..."}
                        className="flex-1 bg-transparent border-none outline-none text-base text-foreground placeholder:text-muted-foreground"
                    />
                    <button
                        onClick={close}
                        className="p-2 -mr-2 rounded-lg hover:bg-muted transition cursor-pointer"
                        aria-label="Close search"
                    >
                        <X className="w-5 h-5 text-foreground" />
                    </button>
                </div>
                {resultsContent}
            </div>

            {/* Desktop: inline dropdown */}
            <div ref={containerRef} className="relative hidden md:block">
                <div
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg backdrop-blur-md bg-white/15 border border-white/20 shadow-lg ${textClass}`}
                >
                    <Search className="w-5 h-5 shrink-0 opacity-60" />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={labels.common?.search || "Search..."}
                        className="bg-transparent border-none outline-none text-sm w-48 placeholder:opacity-50"
                        style={{ color: "inherit" }}
                    />
                    <button onClick={close} className={`p-1 rounded ${hoverBgClass} transition cursor-pointer`}>
                        <X className="w-4 h-4" />
                    </button>
                </div>
                {resultsContent}
            </div>
        </>
    );
}
